import React, { useState, useEffect } from 'react';
import { api } from '../services/apiClient'; // IMPORT API
import { Search, Activity, Target, AlertTriangle, Calculator, Info, Sliders } from 'lucide-react';
import { useTranslation } from 'react-i18next'; 
import ProPaywall from '../components/ProPaywall'; // IMPORT PAYWALL

const ValuationGauge = ({ title, currentPrice, fairValue, mos, description, children }) => {
    const { t } = useTranslation();

    if (!fairValue || fairValue <= 0 || mos === -999) return (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm opacity-60 flex flex-col justify-center h-full min-h-[220px]">
            <h3 className="font-black text-slate-800 text-lg mb-2">{title}</h3>
            <p className="text-sm text-slate-500 mb-4">{description}</p>
            <div className="bg-slate-50 p-4 rounded-xl text-center text-slate-400 font-medium">{t('valuation.gauge_invalid')}</div>
        </div>
    );

    const isUndervalued = currentPrice < fairValue;
    const maxVal = Math.max(currentPrice, fairValue) * 1.2;
    const pricePct = (currentPrice / maxVal) * 100;
    const fairPct = (fairValue / maxVal) * 100;

    return (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col justify-between h-full min-h-[280px]">
            <div>
                <div className="flex justify-between items-start mb-2 gap-4">
                    <h3 className="font-black text-slate-800 text-lg leading-tight">{title}</h3>
                    <div className={`px-3 py-1.5 rounded-xl text-sm font-bold shrink-0 shadow-sm ${isUndervalued ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                        {mos > 0 ? '+' : ''}{mos.toFixed(1)}%
                    </div>
                </div>
                <p className="text-xs text-slate-500 mb-8 leading-relaxed">{description}</p>
            </div>

            <div>
                {/* Barre Visuelle */}
                <div className="relative h-4 bg-slate-100 rounded-full mb-8 mx-2 mt-4">
                    <div className="absolute top-0 left-0 h-full bg-emerald-100 rounded-l-full" style={{ width: `${fairPct}%` }}></div>
                    <div className="absolute top-0 h-full bg-rose-100 rounded-r-full" style={{ left: `${fairPct}%`, width: `${100 - fairPct}%` }}></div>
                    
                    {/* Juste Prix */}
                    <div className="absolute top-1/2 -translate-y-1/2 w-1 h-8 bg-indigo-600 rounded-full z-10 transition-all duration-300" style={{ left: `${fairPct}%` }}></div>
                    <div className="absolute -top-6 text-[10px] font-black text-indigo-600 -translate-x-1/2 whitespace-nowrap transition-all duration-300" style={{ left: `${fairPct}%` }}>{t('valuation.fair_value')} ${fairValue.toFixed(2)}</div>
                    
                    {/* Prix Actuel */}
                    <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-slate-900 rounded-full border-2 border-white z-20 shadow-md" style={{ left: `${pricePct}%` }}></div>
                    <div className="absolute -bottom-6 text-[10px] font-black text-slate-900 -translate-x-1/2 whitespace-nowrap" style={{ left: `${pricePct}%` }}>{t('valuation.current_price')} ${currentPrice.toFixed(2)}</div>
                </div>
                
                {/* Zone Interactive (Sliders) */}
                {children}
            </div>
        </div>
    );
};

const ValuationPage = () => {
    const { t } = useTranslation(); 
    const [searchInput, setSearchInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [isProUser, setIsProUser] = useState(false); // NOUVEAU

    // États Interactifs (Sliders)
    const [grahamG, setGrahamG] = useState(4);
    const [targetPE, setTargetPE] = useState(15);
    const [sectorPE, setSectorPE] = useState(15);
    const [dcfG, setDcfG] = useState(5);
    const [dcfR, setDcfR] = useState(10);
    const [dcfTermPE, setDcfTermPE] = useState(15);

    useEffect(() => {
        const checkProStatus = async () => {
             try {
                const token = localStorage.getItem('token');
                if (token) {
                    const userRes = await api.get('/auth/me');
                    setIsProUser(userRes.data.is_pro);
                }
            } catch (error) {
                console.error("Erreur Auth:", error);
            }
        };
        checkProStatus();
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        const sym = searchInput.trim().toUpperCase();
        if (!sym) return;

        setLoading(true);
        setData(null);
        try {
            const res = await fetch(`/api/valuation/${sym}`);
            if (res.ok) {
                const valuationData = await res.json();
                setData(valuationData);
                
                if (valuationData.metrics) {
                    setGrahamG(valuationData.metrics.growth_rate_used || 4);
                    setTargetPE(valuationData.metrics.avg_pe_5y || 15);
                    setSectorPE(valuationData.metrics.median_peer_pe || 15);
                    setDcfG(valuationData.metrics.growth_rate_used || 5);
                    setDcfR(10); 
                    setDcfTermPE(valuationData.metrics.avg_pe_5y || 15);
                }
            } else {
                alert(`${sym} ${t('valuation.alert_not_found')}`);
            }
        } catch (error) {
            console.error(error);
            alert(t('valuation.alert_error'));
        } finally {
            setLoading(false);
        }
    };

    // --- MOTEUR DE CALCUL DYNAMIQUE ---
    const eps = data?.metrics?.eps || 0;
    const price = data?.price || 0;

    const dynGraham = eps > 0 ? eps * (8.5 + 2 * grahamG) : 0;
    const mosGraham = dynGraham > 0 ? ((dynGraham - price) / dynGraham) * 100 : -999;

    const dynHistPE = eps > 0 ? eps * targetPE : 0;
    const mosHistPE = dynHistPE > 0 ? ((dynHistPE - price) / dynHistPE) * 100 : -999;

    const dynComps = eps > 0 ? eps * sectorPE : 0;
    const mosComps = dynComps > 0 ? ((dynComps - price) / dynComps) * 100 : -999;

    let dynDCF = 0;
    if (eps > 0) {
        let pv = 0;
        let futureEps = eps;
        for (let t = 1; t <= 5; t++) {
            futureEps *= (1 + (dcfG / 100));
            pv += futureEps / Math.pow(1 + (dcfR / 100), t);
        }
        const terminalValue = futureEps * dcfTermPE;
        const pvTerminal = terminalValue / Math.pow(1 + (dcfR / 100), 5);
        dynDCF = pv + pvTerminal;
    }
    const mosDCF = dynDCF > 0 ? ((dynDCF - price) / dynDCF) * 100 : -999;

    // --- VERDICT GLOBAL DYNAMIQUE ---
    let validModels = 0;
    let undervaluedCount = 0;
    const margins = [mosDCF, mosGraham, mosHistPE, mosComps];
    margins.forEach(m => {
        if (m !== -999 && !isNaN(m)) {
            validModels++;
            if (m > 0) undervaluedCount++;
        }
    });

    const validVals = [dynDCF, dynGraham, dynHistPE, dynComps].filter(v => v > 0);
    const avgFairValue = validVals.length > 0 ? validVals.reduce((a,b)=>a+b,0) / validVals.length : 0;
    const mosAvg = avgFairValue > 0 ? ((avgFairValue - price) / avgFairValue) * 100 : -999;

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 font-sans text-slate-900">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* RECHERCHE */}
                <div className="bg-white rounded-3xl p-8 mb-8 border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 flex items-center gap-2 w-fit"><Sliders size={14}/> {t('valuation.badge')}</span>
                            <h1 className="text-3xl md:text-4xl font-black text-slate-900 mt-4 flex items-center gap-3"><Target className="text-emerald-500"/> {t('valuation.title')}</h1>
                            <p className="text-slate-500 mt-2 text-sm font-medium">{t('valuation.subtitle')}</p>
                        </div>
                        <div className="w-full md:w-1/2">
                            <form onSubmit={handleSearch} className="relative">
                                <input 
                                    type="text" 
                                    placeholder={t('valuation.placeholder')} 
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-4 py-4 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500 uppercase transition-all shadow-inner"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    disabled={loading}
                                />
                                <Search className="absolute left-4 top-4 text-slate-400" size={20} />
                                <button type="submit" className="absolute right-2 top-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 font-bold disabled:opacity-50">
                                    {loading ? <Activity size={20} className="animate-spin" /> : <><Calculator size={18}/> {t('valuation.btn_analyze')}</>}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {!isProUser ? (
                    <ProPaywall 
                        title="Modélisation Financière PRO" 
                        description="Calculez la véritable valeur intrinsèque d'une action. Accédez aux 4 modèles mathématiques interactifs (DCF, Benjamin Graham, PER Historique, Comparables) pour savoir à quel prix acheter." 
                    />
                ) : (
                    data && (
                        <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
                            <div className="text-center">
                                <h2 className="text-2xl font-black text-slate-800">{data.companyName} ({data.symbol})</h2>
                                <div className="text-5xl font-mono font-black mt-2 text-slate-900">${data.price}</div>
                                <div className="text-sm font-bold text-slate-400 mt-2">{t('valuation.eps_base')} ${eps}</div>
                            </div>

                            {/* GRILLE DES 4 MODELES INTERACTIFS */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                
                                <ValuationGauge 
                                    title={t('valuation.dcf_title')} 
                                    description={t('valuation.dcf_desc')}
                                    currentPrice={price} fairValue={dynDCF} mos={mosDCF}
                                >
                                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                                        <div>
                                            <label className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">{t('valuation.dcf_growth')} <span className="text-indigo-600">{dcfG.toFixed(1)}%</span></label>
                                            <input type="range" min="-10" max="50" step="0.5" value={dcfG} onChange={e=>setDcfG(Number(e.target.value))} className="w-full accent-indigo-600 h-1" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">{t('valuation.dcf_discount')} <span className="text-indigo-600">{dcfR.toFixed(1)}%</span></label>
                                                <input type="range" min="5" max="25" step="0.5" value={dcfR} onChange={e=>setDcfR(Number(e.target.value))} className="w-full accent-indigo-600 h-1" />
                                            </div>
                                            <div>
                                                <label className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">{t('valuation.dcf_terminal')} <span className="text-indigo-600">{dcfTermPE}x</span></label>
                                                <input type="range" min="5" max="60" step="1" value={dcfTermPE} onChange={e=>setDcfTermPE(Number(e.target.value))} className="w-full accent-indigo-600 h-1" />
                                            </div>
                                        </div>
                                    </div>
                                </ValuationGauge>

                                <ValuationGauge 
                                    title={t('valuation.graham_title')} 
                                    description={t('valuation.graham_desc')}
                                    currentPrice={price} fairValue={dynGraham} mos={mosGraham}
                                >
                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        <label className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">{t('valuation.graham_growth')} <span className="text-indigo-600">{grahamG.toFixed(1)}%</span></label>
                                        <input type="range" min="-5" max="30" step="0.5" value={grahamG} onChange={e => setGrahamG(Number(e.target.value))} className="w-full accent-indigo-600 h-1" />
                                        <div className="text-[10px] text-slate-400 mt-1 text-center">{t('valuation.graham_default')} {data.metrics?.growth_rate_used}%</div>
                                    </div>
                                </ValuationGauge>

                                <ValuationGauge 
                                    title={t('valuation.histpe_title')} 
                                    description={t('valuation.histpe_desc')}
                                    currentPrice={price} fairValue={dynHistPE} mos={mosHistPE}
                                >
                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        <label className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">{t('valuation.histpe_target')} <span className="text-indigo-600">{targetPE.toFixed(1)}x</span></label>
                                        <input type="range" min="5" max="100" step="1" value={targetPE} onChange={e => setTargetPE(Number(e.target.value))} className="w-full accent-indigo-600 h-1" />
                                        <div className="text-[10px] text-slate-400 mt-1 text-center">{t('valuation.histpe_avg')} {data.metrics?.avg_pe_5y}x</div>
                                    </div>
                                </ValuationGauge>

                                <ValuationGauge 
                                    title={t('valuation.comp_title')} 
                                    description={t('valuation.comp_desc')}
                                    currentPrice={price} fairValue={dynComps} mos={mosComps}
                                >
                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        <label className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">{t('valuation.comp_sector')} <span className="text-indigo-600">{sectorPE.toFixed(1)}x</span></label>
                                        <input type="range" min="5" max="100" step="1" value={sectorPE} onChange={e => setSectorPE(Number(e.target.value))} className="w-full accent-indigo-600 h-1" />
                                        <div className="text-[10px] text-slate-400 mt-1 text-center">{t('valuation.comp_median', { count: data.metrics?.peers_used })} {data.metrics?.median_peer_pe}x</div>
                                    </div>
                                </ValuationGauge>

                            </div>
                            
                            {/* VERDICT GLOBAL DYNAMIQUE */}
                            <div className="bg-slate-900 text-white p-8 rounded-3xl mt-8 flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden shadow-xl border border-slate-800 transition-all duration-500">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                                
                                <div className="flex items-center gap-6 relative z-10 w-full md:w-auto">
                                    <div className="bg-white/10 p-5 rounded-2xl shrink-0 transition-colors duration-500">
                                        {mosAvg > 0 ? <Target size={40} className="text-emerald-400"/> : <AlertTriangle size={40} className="text-rose-400"/>}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-400 text-sm uppercase tracking-widest mb-1">{t('valuation.verdict_title')}</h3>
                                        <p className="text-slate-100 text-sm leading-relaxed">
                                            <strong className="text-white text-lg">{t('valuation.verdict_models', { undervalued: undervaluedCount, total: validModels })}</strong> {t('valuation.verdict_discount')}<br/>
                                            {mosAvg > 15 ? 
                                                t('valuation.verdict_good') : 
                                             mosAvg < -10 ? 
                                                t('valuation.verdict_bad') : 
                                                t('valuation.verdict_neutral')}
                                        </p>
                                    </div>
                                </div>

                                {/* LE JUSTE PRIX MOYEN */}
                                <div className="relative z-10 bg-white/5 border border-white/10 p-6 rounded-2xl text-center shrink-0 w-full md:w-64">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-2">{t('valuation.avg_title')}</span>
                                    <div className="text-4xl font-black font-mono text-white mb-2 transition-all duration-300">${avgFairValue.toFixed(2)}</div>
                                    <div className={`inline-block px-3 py-1 rounded-lg text-xs font-bold transition-all duration-300 ${mosAvg > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                                        {t('valuation.avg_margin')} {mosAvg > 0 ? '+' : ''}{mosAvg.toFixed(1)}%
                                    </div>
                                </div>
                            </div>

                            <div className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-2">
                                <Info size={14}/> {t('valuation.footer_note')}
                            </div>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default ValuationPage;
